import { observable, computed } from '@/libs/state'
import type { SheetEntry, SheetId } from './domain/ui'
import { GLOBAL_SHEET_IDS } from '@/features/ui/hooks/use-sheet'

type URLParams = {
	// customers
	customerId: string | null
	createCustomer: boolean | null
	// hr
	viewMemberId: string | null
	memberId: string | null
	createMember: boolean | null
	// shared table filter — cleared when sheets close
	q: string | null
}

// Updates sheet-related query params directly via history.replaceState,
// bypassing TanStack Router's navigate() which re-runs beforeLoad guards.
const updateSheetParams = (params: Partial<URLParams>): void => {
	const url = new URL(window.location.href)
	for (const [key, value] of Object.entries(params)) {
		if (value === null || value === undefined) url.searchParams.delete(key)
		else url.searchParams.set(key, String(value))
	}
	window.history.replaceState(window.history.state, '', url.toString())
}


const sheetStack$ = observable<SheetEntry[]>([])

const isSomeSheetOpen$ = computed(() => sheetStack$.get().length > 0)

const activeSheet$ = computed(() => {
	const stack = sheetStack$.get()
	return stack.length > 0 ? stack[stack.length - 1] : null
})

const isSheetOpen = (id: SheetId): boolean => sheetStack$.get().some(s => s.id === id)

const getSheetEntityId = (id: SheetId): string | null => sheetStack$.get().find(s => s.id === id)?.entityId ?? null

const sheetToParams = (id: SheetId, entityId: string | null): Partial<URLParams> => {
	switch (id) {
		case GLOBAL_SHEET_IDS.CUSTOMER_EDIT:
			return { customerId: entityId }
		case GLOBAL_SHEET_IDS.CUSTOMER_CREATE:
			return { createCustomer: true }
		case GLOBAL_SHEET_IDS.MEMBER_DETAILS:
			return { viewMemberId: entityId }
		case GLOBAL_SHEET_IDS.MEMBER_EDIT:
			return { memberId: entityId }
		case GLOBAL_SHEET_IDS.MEMBER_CREATE:
			return { createMember: true }
	}
}

const sheetClearParams = (id: SheetId): Partial<URLParams> => {
	switch (id) {
		case GLOBAL_SHEET_IDS.CUSTOMER_EDIT:
			return { customerId: null, q: null }
		case GLOBAL_SHEET_IDS.CUSTOMER_CREATE:
			return { createCustomer: null }
		case GLOBAL_SHEET_IDS.MEMBER_DETAILS:
			return { viewMemberId: null, q: null }
		case GLOBAL_SHEET_IDS.MEMBER_EDIT:
			return { memberId: null, q: null }
		case GLOBAL_SHEET_IDS.MEMBER_CREATE:
			return { createMember: null }
	}
}

const openSheet = (id: SheetId, entityId: string | null = null): void => {
	const stack = sheetStack$.get()
	if (stack.some(s => s.id === id)) return
	sheetStack$.set([...stack, { id, entityId }])
	updateSheetParams(sheetToParams(id, entityId))
}

const closeSheet = (id: SheetId): void => {
	sheetStack$.set(sheetStack$.get().filter(s => s.id !== id))
	updateSheetParams(sheetClearParams(id))
}

const closeAllSheets = (): void => {
	const stack = sheetStack$.get()
	const clearAll = stack.reduce<Partial<URLParams>>((acc, entry) => ({ ...acc, ...sheetClearParams(entry.id) }), {})
	sheetStack$.set([])
	updateSheetParams(clearAll)
}

// Called on mount to hydrate stack from URL params
const hydrateFromURL = (params: {
	customerId?: string | null
	createCustomer?: boolean | null
	viewMemberId?: string | null
	memberId?: string | null
	createMember?: boolean | null
}): void => {
	const entries: SheetEntry[] = []
	if (params.createCustomer) entries.push({ id: GLOBAL_SHEET_IDS.CUSTOMER_CREATE, entityId: null })
	if (params.customerId) entries.push({ id: GLOBAL_SHEET_IDS.CUSTOMER_EDIT, entityId: params.customerId })
	if (params.createMember) entries.push({ id: GLOBAL_SHEET_IDS.MEMBER_CREATE, entityId: null })
	if (params.viewMemberId) entries.push({ id: GLOBAL_SHEET_IDS.MEMBER_DETAILS, entityId: params.viewMemberId })
	if (params.memberId) entries.push({ id: GLOBAL_SHEET_IDS.MEMBER_EDIT, entityId: params.memberId })
	sheetStack$.set(entries)
}

export {
	sheetStack$,
	isSomeSheetOpen$,
	activeSheet$,
	isSheetOpen,
	getSheetEntityId,
	openSheet,
	closeSheet,
	closeAllSheets,
	hydrateFromURL,
}
